import { useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { MRT_ColumnFiltersState } from "material-react-table";
import {
  PAGE_INDEX_QUERY_NAME,
  PAGE_SIZE_QUERY_NAME,
  SEARCH_QUERY_NAME,
  PAGE_SIZE_QUERY_ALL_VALUE,
  INITIAL_PAGE_SIZE_QUERY_VALUE,
  PAGE_SIZE_QUERY_NAME_ALL,
  TABS_QUERY_NAME,
} from "@techzyapp/ui-lib";

interface UseTableFilterProps {
  initialPage: number;
  initialSize: number;
  initialSearch: string;
  initialFilters: MRT_ColumnFiltersState;
  setPagination: (pageIndex: number, pageSize: number) => void;
  setSearch: (key: string) => void;
  setFilters: (filters: MRT_ColumnFiltersState) => void;
}

export const useTableFilter = (): UseTableFilterProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialPage = Number(searchParams.get(PAGE_INDEX_QUERY_NAME)) || 1;
  const initialSize =
    searchParams.get(PAGE_SIZE_QUERY_NAME) === PAGE_SIZE_QUERY_NAME_ALL
      ? PAGE_SIZE_QUERY_ALL_VALUE
      : Number(searchParams.get(PAGE_SIZE_QUERY_NAME)) ||
        INITIAL_PAGE_SIZE_QUERY_VALUE;
  const initialSearch = searchParams.get(SEARCH_QUERY_NAME) || "";
  const initialFilters: MRT_ColumnFiltersState = Array.from(
    searchParams.entries()
  )
    .filter(
      ([key]) =>
        ![
          PAGE_INDEX_QUERY_NAME,
          PAGE_SIZE_QUERY_NAME,
          SEARCH_QUERY_NAME,
          TABS_QUERY_NAME,
        ].includes(key)
    )
    .reduce<MRT_ColumnFiltersState>((acc, [key, value]) => {
      const match = key.match(/^(.+)\[(\d+)\]$/); // Matches key[index] format
      if (match) {
        const [, baseKey, index] = match;
        let filter = acc.find((filter) => filter.id === baseKey);

        if (!filter) {
          // Dynamically decide the type of the filter based on its key
          const isStringArray = value && isNaN(Number(value)); // If value is not a number, treat it as string[]

          filter = {
            id: baseKey,
            value: isStringArray ? ([] as string[]) : ([] as number[]), // Dynamically assign the type
          };
          acc.push(filter);
        }

        // Assign value to the filter array
        if (Array.isArray(filter.value)) {
          if (isNaN(Number(value))) {
            (filter.value as string[])[Number(index)] = value; // Assign as string if not a number
          } else {
            (filter.value as number[])[Number(index)] = Number(value); // Convert to number if applicable
          }
        }
      } else {
        // For non-array filters, handle them as usual
        let parsedValue: string | number = isNaN(Number(value))
          ? value
          : Number(value);
        acc.push({
          id: key,
          value: parsedValue,
        });
      }

      return acc;
    }, []);

  useEffect(() => {
    if (
      !searchParams.has(PAGE_INDEX_QUERY_NAME) &&
      !searchParams.has(PAGE_SIZE_QUERY_NAME)
    ) {
      searchParams.set(PAGE_INDEX_QUERY_NAME, "1");
      searchParams.set(
        PAGE_SIZE_QUERY_NAME,
        INITIAL_PAGE_SIZE_QUERY_VALUE.toString()
      );
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );
    }
  }, [searchParams, navigate]);

  const setPagination = (pageIndex: number, pageSize: number | string) => {
    pageSize === PAGE_SIZE_QUERY_ALL_VALUE
      ? searchParams.delete(PAGE_INDEX_QUERY_NAME)
      : searchParams.set(PAGE_INDEX_QUERY_NAME, `${pageIndex}`);

    searchParams.set(
      PAGE_SIZE_QUERY_NAME,
      pageSize === PAGE_SIZE_QUERY_ALL_VALUE
        ? PAGE_SIZE_QUERY_NAME_ALL
        : `${pageSize}`
    );

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const setSearch = (key: string) => {
    if (key) searchParams.set(SEARCH_QUERY_NAME, key);
    else searchParams.delete(SEARCH_QUERY_NAME);

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const setFilters = (filters: MRT_ColumnFiltersState) => {
    Array.from(searchParams.keys()).forEach((key) => {
      if (
        key !== PAGE_INDEX_QUERY_NAME &&
        key !== PAGE_SIZE_QUERY_NAME &&
        key !== SEARCH_QUERY_NAME &&
        key !== TABS_QUERY_NAME
      ) {
        searchParams.delete(key);
      }
    });

    filters.forEach((filter) => {
      const { id, value } = filter;

      if (Array.isArray(value)) {
        value.forEach((v, index) => {
          searchParams.set(`${id}[${index}]`, String(v));
        });
      } else {
        searchParams.set(id, String(value));
      }
    });

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  return {
    initialPage,
    initialSize,
    initialSearch,
    initialFilters,
    setPagination,
    setSearch,
    setFilters,
  };
};

export default useTableFilter;
